/**
 * LR Direct — CANONICAL milestone vocabulary + next-action derivation.
 *
 * Single source of truth for the broker/carrier dispatch workflow. Both the
 * progress tracker and the "NEXT UP" call-to-action read from here, so the
 * timeline and the button can never disagree.
 *
 * The milestone list itself comes from `deriveStages()` (events +
 * direct_dispatch_stops). `direct_dispatches.status` is treated as a derived
 * denormalisation for badges/lists/notifications only — see
 * `legacyStatusForStages()`.
 */
import type { DerivedStage } from "@/lib/lrDirect/deriveStages";
import type { DispatchStatus } from "@/hooks/useUpdateDispatchStatus";

/** Stages the broker/carrier can actually action. */
export function isActionableStage(s: DerivedStage): boolean {
  return s.kind === "broker_stop" || s.kind === "broker_dispatch";
}

/** First incomplete actionable milestone — the one NEXT UP points at. */
export function nextActionableStage(stages: DerivedStage[]): DerivedStage | null {
  return stages.find((s) => isActionableStage(s) && s.status !== "done") ?? null;
}

/** Canonical action verb + short explanation for a milestone. */
export function actionForStage(stage: DerivedStage): { verb: string; hint: string } {
  if (stage.kind === "broker_dispatch") {
    if (stage.key === "truck_en_route") {
      return {
        verb: "Mark Truck En Route",
        hint: "Let us know when the truck is rolling to the pickup.",
      };
    }
    return {
      verb: "Mark In Transit",
      hint: "Let us know when the truck is rolling to the delivery.",
    };
  }
  if (stage.stopType === "pickup") {
    return stage.subKind === "checked_in"
      ? { verb: "Check In at Pickup", hint: "Let us know when you arrive at the pickup location." }
      : { verb: "Mark Pickup Loaded", hint: "Let us know once the freight is loaded." };
  }
  return stage.subKind === "checked_in"
    ? { verb: "Check In at Delivery", hint: "Let us know when you arrive at the delivery location." }
    : { verb: "Mark Delivered", hint: "Let us know once the freight is delivered." };
}

/**
 * Compatibility adapter: legacy `direct_dispatches.status` value implied by the
 * canonical milestone state. Written as a side effect after every set/undo so
 * badges, My Loads buckets and notifications stay correct without letting the
 * legacy column drive the visible timeline.
 */
export function legacyStatusForStages(
  stages: DerivedStage[],
  opts: { accepted: boolean; closed: boolean },
): DispatchStatus {
  if (opts.closed) return "closed";
  const done = (key: string) => stages.find((s) => s.key === key)?.status === "done";
  const anyDone = (pred: (s: DerivedStage) => boolean) =>
    stages.some((s) => pred(s) && s.status === "done");

  if (done("delivered")) return "delivered";
  if (anyDone((s) => s.stopType === "delivery" && s.subKind === "checked_in")) return "at_delivery";
  if (done("in_transit")) return "en_route_to_delivery";
  if (anyDone((s) => s.stopType === "pickup" && s.subKind === "done")) return "picked_up";
  if (anyDone((s) => s.stopType === "pickup" && s.subKind === "checked_in")) return "at_pickup";
  if (done("truck_en_route")) return "en_route_to_pickup";
  return opts.accepted ? "assigned" : "ready";
}
